import { SupabaseClient } from '@supabase/supabase-js'

interface APIConnection {
  id: string
  project_id: string
  name: string
  description?: string
  base_url: string
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  endpoint_path: string
  auth_type: 'none' | 'api_key' | 'bearer'
  auth_header?: string
  parameters_schema: any
  is_enabled: boolean
  created_by: string
  created_at: string
  updated_at: string
}

interface CreateAPIConnectionDto {
  name: string
  description?: string
  base_url: string
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  endpoint_path: string
  auth_type: 'none' | 'api_key' | 'bearer'
  auth_header?: string
  parameters_schema?: any
}

interface UpdateAPIConnectionDto extends Partial<CreateAPIConnectionDto> {
  is_enabled?: boolean
}

export class APIConnectionService {
  constructor(private supabase: SupabaseClient) {}
  /**
   * List all API connections for a project
   */
  async listConnections(projectId: string): Promise<APIConnection[]> {
    const { data, error } = await this.supabase
      .from('api_connections')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Failed to list API connections:', error)
      throw error
    }

    return data || []
  }

  /**
   * Get a single API connection
   */
  async getConnection(connectionId: string): Promise<APIConnection | null> {
    const { data, error } = await this.supabase
      .from('api_connections')
      .select('*')
      .eq('id', connectionId)
      .single()

    if (error) {
      console.error('Failed to get API connection:', error)
      throw error
    }

    return data
  }

  /**
   * Create a new API connection
   */
  async createConnection(
    projectId: string,
    userId: string,
    dto: CreateAPIConnectionDto
  ): Promise<APIConnection> {
    const { data, error } = await this.supabase
      .from('api_connections')
      .insert({
        project_id: projectId,
        created_by: userId,
        ...dto,
        parameters_schema: dto.parameters_schema || { type: 'object', properties: {} }
      })
      .select()
      .single()

    if (error) {
      console.error('Failed to create API connection:', error)
      throw error
    }

    return data
  }

  /**
   * Update an API connection
   */
  async updateConnection(
    connectionId: string,
    dto: UpdateAPIConnectionDto
  ): Promise<APIConnection> {
    const { data, error } = await this.supabase
      .from('api_connections')
      .update({
        ...dto,
        updated_at: new Date().toISOString()
      })
      .eq('id', connectionId)
      .select()
      .single()

    if (error) {
      console.error('Failed to update API connection:', error)
      throw error
    }

    return data
  }

  /**
   * Delete an API connection
   */
  async deleteConnection(connectionId: string): Promise<void> {
    const { error } = await this.supabase
      .from('api_connections')
      .delete()
      .eq('id', connectionId)

    if (error) {
      console.error('Failed to delete API connection:', error)
      throw error
    }
  }

  /**
   * Toggle API connection enabled status
   */
  async toggleEnabled(connectionId: string, enabled: boolean): Promise<void> {
    const { error } = await this.supabase
      .from('api_connections')
      .update({
        is_enabled: enabled,
        updated_at: new Date().toISOString()
      })
      .eq('id', connectionId)

    if (error) {
      console.error('Failed to toggle API connection:', error)
      throw error
    }
  }

  /**
   * Test an API connection
   */
  async testConnection(connectionId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const connection = await this.getConnection(connectionId)
      if (!connection) {
        return { success: false, message: 'Connection not found' }
      }

      // Build the full URL
      const url = `${connection.base_url}${connection.endpoint_path}`
      
      // Prepare headers
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      
      // Add authentication header if needed
      if (connection.auth_type !== 'none' && connection.auth_header) {
        // Parse the auth header (format: "Authorization: Bearer xxx" or "X-API-Key: xxx")
        const [headerName, headerValue] = connection.auth_header.split(':').map(s => s.trim())
        if (headerName && headerValue) {
          headers[headerName] = headerValue
        }
      }

      // Make test request
      const response = await fetch(url, {
        method: connection.method,
        headers,
        // For GET requests, we don't send a body
        ...(connection.method !== 'GET' && {
          body: JSON.stringify({})
        })
      })

      if (response.ok) {
        return { success: true, message: `Connection successful (${response.status})` }
      } else {
        return { success: false, message: `Connection failed (${response.status})` }
      }
    } catch (error) {
      console.error('Failed to test connection:', error)
      return { success: false, message: error.message }
    }
  }

  /**
   * Get enabled API connections for a project (for AI assistant)
   */
  async getEnabledConnections(projectId: string): Promise<APIConnection[]> {
    const { data, error } = await this.supabase
      .from('api_connections')
      .select('*')
      .eq('project_id', projectId)
      .eq('is_enabled', true)

    if (error) {
      console.error('Failed to get enabled API connections:', error)
      throw error
    }

    return data || []
  }

  /**
   * Log API execution
   */
  async logExecution(
    connectionId: string,
    projectId: string,
    userId: string,
    executionData: {
      roomId?: string
      chatId?: string
      requestParams?: any
      requestHeaders?: any
      responseData?: any
      responseStatus?: number
      errorMessage?: string
      durationMs?: number
    }
  ): Promise<void> {
    const { error } = await this.supabase
      .from('api_executions')
      .insert({
        connection_id: connectionId,
        project_id: projectId,
        executed_by: userId,
        room_id: executionData.roomId,
        chat_id: executionData.chatId,
        request_params: executionData.requestParams,
        request_headers: executionData.requestHeaders,
        response_data: executionData.responseData,
        response_status: executionData.responseStatus,
        error_message: executionData.errorMessage,
        duration_ms: executionData.durationMs
      })

    if (error) {
      console.error('Failed to log API execution:', error)
      // Don't throw - logging failures shouldn't break the flow
    }
  }
}

export type { APIConnection, CreateAPIConnectionDto, UpdateAPIConnectionDto }